import React, { useState } from 'react';
import {
  ArrowRight,
  Shield,
  Truck,
  Crosshair,
  RectangleHorizontal,
  Phone,
  FileText,
  BadgeCheck,
  Wrench,
  Star,
  Quote,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import Navbar from '../components/site/Navbar';
import Footer from '../components/site/Footer';
import QuoteRequestModal from '../components/site/QuoteRequestModal';
import MobileLanding from '../components/mobile/MobileLanding';
import useIsMobile from '../hooks/useIsMobile';
import { useSeo, useJsonLd, faqSchema } from '../lib/seo';
import { useGoogleReviews } from '../components/mobile/shared';

const HERO_IMAGE = 'https://customer-assets.emergentagent.com/job_glass-quote-hub-1/artifacts/1cc3qzta_Van%20with%20Parliment.png';
const INDIVIDUALS_IMAGE = 'https://customer-assets-lxgj4vgw.emergentagent.net/job_glass-quote-hub-1/artifacts/96957399623cb06d_windshield%20install.png';
const DEALERS_IMAGE = 'https://customer-assets-lxgj4vgw.emergentagent.net/job_glass-quote-hub-1/artifacts/4b5b320eb93bab25_dealership.webp';

const FEATURES = [
  { icon: RectangleHorizontal, title: 'WINDSHIELD REPLACEMENT', desc: 'All makes and models.' },
  { icon: Crosshair, title: 'ADAS CALIBRATION', desc: 'Advanced systems.' },
  { icon: Truck, title: 'MOBILE SERVICE', desc: 'At your home or work.' },
  { icon: Shield, title: 'INSURANCE APPROVED', desc: 'We work with all major providers.' },
  { icon: BadgeCheck, title: 'LIFETIME WARRANTY', desc: 'Installation warranty.' },
];

const SERVICES = [
  {
    img: 'https://static.prod-images.emergentagent.com/jobs/2f54c576-01e2-4664-be6d-66ca282e4aae/images/95b357476ab0d5a057ffd3babf1760adb50ca339cf300e9520cb936c8a34dfbb.jpeg',
    icon: RectangleHorizontal,
    title: 'WINDSHIELD REPLACEMENT',
    desc: 'Professional windshield replacement in Ottawa and Orl\u00e9ans. Quality glass, expert installation and safe, reliable service for all vehicle makes and models.',
  },
  {
    img: 'https://static.prod-images.emergentagent.com/jobs/2f54c576-01e2-4664-be6d-66ca282e4aae/images/469d8be22592634b1869b7efa39e66620bd29ec7f7368f208725960671a2b5b0.jpeg',
    icon: Wrench,
    title: 'ROCK CHIP REPAIR',
    desc: 'Stop a small chip before it spreads — done in 30 minutes.',
  },
  {
    img: 'https://static.prod-images.emergentagent.com/jobs/2f54c576-01e2-4664-be6d-66ca282e4aae/images/631e077f209b205c012dcaf2ccd802d3a68d9c432002175c6a7309bf34de6770.jpeg',
    icon: Crosshair,
    title: 'ADAS CALIBRATION',
    desc: 'Professional ADAS camera calibration in Ottawa and Orl\u00e9ans. Precise calibration of vehicle safety systems after windshield replacement for safe, reliable driving.',
  },
  {
    img: 'https://static.prod-images.emergentagent.com/jobs/2f54c576-01e2-4664-be6d-66ca282e4aae/images/3616a9288e90c82b765e99690effd133eb0d9c5bb1fb17e3b5a75c2d197b6837.jpeg',
    icon: Truck,
    title: 'MOBILE SERVICE',
    desc: 'Convenient mobile windshield replacement in Ottawa and Orl\u00e9ans. Our expert technicians come to your home or workplace for professional auto glass service.',
  },
  {
    img: 'https://static.prod-images.emergentagent.com/jobs/2f54c576-01e2-4664-be6d-66ca282e4aae/images/6e79e697170687320cd00a20712cbdd922f222403dfa7917a3611e32f0688432.jpeg',
    icon: Shield,
    title: 'INSURANCE CLAIMS',
    desc: 'We handle the process so you don\u2019t have to.',
  },
  {
    img: 'https://static.prod-images.emergentagent.com/jobs/2f54c576-01e2-4664-be6d-66ca282e4aae/images/3e86545f4940b2f40359c62fe869a2e3d5fd9bef4af961d83a155ef8e8aeb52d.jpeg',
    icon: BadgeCheck,
    title: 'LIFETIME WARRANTY',
    desc: 'Every installation is backed for as long as you own the vehicle.',
  },
];

export default function Landing() {
  const isMobile = useIsMobile();
  useSeo({
    title: "A+ Auto Glass — Windshield Replacement, Rock Chip Repair & ADAS Calibration in Ottawa",
    description: "Ottawa's trusted auto glass experts. Same/next-day windshield replacement, rock chip repair, ADAS calibration and mobile service across Ottawa, Orleans, Kanata & Gatineau. Call 343-558-8820.",
    path: '/',
  });
  useJsonLd('ld-faq', faqSchema);
  if (isMobile) return <MobileLanding />;
  return <DesktopLanding />;
}

const Stars = ({ n = 5, size = 'w-4 h-4' }) => (
  <div className="flex gap-0.5">
    {Array.from({ length: 5 }).map((_, i) => (
      <Star key={i} className={`${size} ${i < n ? 'fill-[#F5B301] text-[#F5B301]' : 'text-slate-300'}`} />
    ))}
  </div>
);

function DesktopLanding() {
  const [quoteOpen, setQuoteOpen] = useState(false);
  const google = useGoogleReviews();
  const servicesRef = React.useRef(null);

  const scrollServices = (dir) => {
    const el = servicesRef.current;
    if (!el) return;
    const card = el.firstElementChild?.getBoundingClientRect().width || 340;
    el.scrollBy({ left: dir * (card + 28), behavior: 'smooth' });
  };

  React.useEffect(() => {
    const handler = () => setQuoteOpen(true);
    window.addEventListener('open-quote-modal', handler);
    return () => window.removeEventListener('open-quote-modal', handler);
  }, []);

  return (
    <div className="min-h-screen bg-white text-[#111827]">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden bg-[#5a8fc4]">
        <div className="relative h-[560px] md:h-[680px] w-full">
          <img
            src={HERO_IMAGE}
            alt="A+ Auto Glass service van with Ottawa parliament in background"
            className="absolute inset-0 w-full h-full object-cover"
            style={{ objectPosition: 'center 68%' }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/30 to-transparent" />
          <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10 h-full flex items-center">
            <div className="max-w-[680px] text-white">
              <div className="text-[13px] font-bold tracking-[0.26em] text-white/85">OTTAWA AUTO GLASS EXPERTS</div>
              <h1 className="mt-5 max-w-[640px] font-black leading-[1.04] tracking-[-0.015em] text-[46px] md:text-[58px] lg:text-[64px] drop-shadow-[0_3px_18px_rgba(0,0,0,0.4)]">
                Windshield Replacement &amp; Auto Glass Services
              </h1>
              <p className="mt-6 text-[18px] md:text-[20px] text-white/95 leading-[1.55] max-w-[600px] font-medium drop-shadow-[0_2px_10px_rgba(0,0,0,0.45)]">
                Mobile service, professional installation and ADAS calibration.<br />
                We work with all major insurance providers.
              </p>

              <div className="mt-9 flex flex-wrap items-center gap-4">
                <button
                  onClick={() => setQuoteOpen(true)}
                  data-testid="hero-quote-btn"
                  className="group inline-flex items-center gap-4 h-[72px] pl-6 pr-7 rounded-xl bg-[#0D5BD7] hover:bg-[#0a4bb5] text-white transition-all shadow-[0_14px_34px_rgba(13,91,215,0.4)] hover:-translate-y-[2px]"
                >
                  <FileText className="w-6 h-6 shrink-0" strokeWidth={1.8} />
                  <span className="text-left">
                    <span className="block text-[16px] font-bold tracking-[0.06em]">GET A FREE QUOTE</span>
                    <span className="block text-[13px] text-white/80 font-medium">Fast. No obligation.</span>
                  </span>
                  <ArrowRight className="w-5 h-5 ml-2 transition-transform group-hover:translate-x-1" />
                </button>
              </div>

              {/* Google rating */}
              <a
                href={google.maps_url || 'https://www.google.com/search?q=A%2B+Auto+Glass+Ottawa'}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-google-rating"
                className="mt-8 inline-flex items-center gap-3 rounded-full bg-black/35 backdrop-blur-md border border-white/20 px-5 py-2.5 hover:bg-black/50 transition-colors"
              >
                <Stars size="w-[18px] h-[18px]" />
                <span className="text-[15px] font-bold">{Number(google.rating).toFixed(1)}</span>
                <span className="text-[14px] text-white/80">
                  on Google{google.total_reviews ? ` · ${google.total_reviews} reviews` : ''}
                </span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES STRIP */}
      <section className="bg-[#F8F9FB] border-b border-slate-100">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-y-9">
          {FEATURES.map((f, i) => (
            <div
              key={f.title}
              className={`text-center px-5 ${i < FEATURES.length - 1 ? 'lg:border-r lg:border-slate-200' : ''}`}
            >
              <f.icon className="w-8 h-8 mx-auto text-[#0D5BD7]" strokeWidth={1.6} />
              <div className="mt-4 text-[13px] font-bold tracking-[0.12em] text-[#111827]">{f.title}</div>
              <div className="mt-2 text-[14px] text-[#5F6773] leading-[1.6]">{f.desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* AUDIENCE PANELS — full-bleed */}
      <section className="bg-white">
        <div className="grid grid-cols-1 lg:grid-cols-2">
          <AudiencePanel
            testId="panel-individuals"
            image={INDIVIDUALS_IMAGE}
            eyebrow="FOR INDIVIDUALS"
            title={<>A Safer Drive<br />Starts Here</>}
            desc="High-quality glass, expert installation and convenient mobile service."
            ctaLabel="OUR SERVICES"
            href="/services"
            dark={false}
          />
          <AudiencePanel
            testId="panel-dealers"
            image={DEALERS_IMAGE}
            eyebrow="FOR DEALERSHIPS & FLEETS"
            title={<>Reliable Auto Glass<br />Solutions for Your Business</>}
            desc="Dedicated account support, mobile service, calibration and streamlined billing."
            ctaLabel="LEARN MORE"
            href="/dealers"
            dark
          />
        </div>
      </section>

      {/* SERVICES — carousel */}
      <section id="services" className="bg-white py-20">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="text-center">
            <div className="text-[12px] font-bold tracking-[0.28em] text-[#0D5BD7]">OUR SERVICES</div>
            <h2 className="mt-4 text-[34px] md:text-[44px] font-black text-[#111827] tracking-[-0.01em]">
              COMPLETE AUTO GLASS SOLUTIONS
            </h2>
          </div>

          <div className="relative mt-12">
            <button
              type="button"
              onClick={() => scrollServices(-1)}
              aria-label="Previous services"
              data-testid="services-prev-btn"
              className="hidden md:flex absolute -left-5 lg:-left-7 top-1/2 -translate-y-1/2 z-20 w-14 h-14 rounded-full border border-slate-200 bg-white text-[#111827] items-center justify-center hover:bg-[#0D5BD7] hover:border-[#0D5BD7] hover:text-white transition-colors shadow-[0_8px_26px_rgba(15,23,42,0.14)]"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button
              type="button"
              onClick={() => scrollServices(1)}
              aria-label="Next services"
              data-testid="services-next-btn"
              className="hidden md:flex absolute -right-5 lg:-right-7 top-1/2 -translate-y-1/2 z-20 w-14 h-14 rounded-full border border-slate-200 bg-white text-[#111827] items-center justify-center hover:bg-[#0D5BD7] hover:border-[#0D5BD7] hover:text-white transition-colors shadow-[0_8px_26px_rgba(15,23,42,0.14)]"
            >
              <ChevronRight className="w-6 h-6" />
            </button>

            <div
              ref={servicesRef}
              data-testid="services-carousel"
              className="flex gap-7 overflow-x-auto snap-x snap-mandatory pb-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            >
            {SERVICES.map((s) => (
              <article
                key={s.title}
                data-testid={`service-card-${s.title.toLowerCase().replace(/\s+/g, '-')}`}
                className="snap-start shrink-0 w-[340px] lg:w-[calc((100%-56px)/3)] bg-white rounded-2xl overflow-hidden group border border-slate-100 shadow-[0_2px_16px_rgba(15,23,42,0.06)] hover:shadow-[0_22px_48px_rgba(13,91,215,0.14)] hover:-translate-y-1 transition-all duration-300 flex flex-col"
              >
                <div className="relative aspect-[3/2] overflow-hidden bg-slate-100">
                  <img
                    src={s.img}
                    alt={s.title}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.05] transition-transform duration-[650ms]"
                  />
                  <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/55 to-transparent" />
                  <div className="absolute left-5 bottom-4 flex items-center gap-3">
                    <span className="w-10 h-10 rounded-full bg-[#0D5BD7] flex items-center justify-center shadow-lg">
                      <s.icon className="w-5 h-5 text-white" strokeWidth={2} />
                    </span>
                    <h3 className="text-white text-[16px] font-bold tracking-[0.08em] drop-shadow-[0_2px_8px_rgba(0,0,0,0.6)]">
                      {s.title}
                    </h3>
                  </div>
                </div>
                <div className="px-6 py-6">
                  <p className="text-[15px] text-[#5F6773] leading-[1.65]">{s.desc}</p>
                </div>
              </article>
            ))}
            </div>
          </div>
        </div>
      </section>

      {/* REVIEWS */}
      <section className="bg-[#F8F9FB] py-20" data-testid="desktop-reviews">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <div>
              <div className="text-[12px] font-bold tracking-[0.28em] text-[#0D5BD7]">GOOGLE REVIEWS</div>
              <h2 className="mt-4 text-[34px] md:text-[44px] font-black text-[#111827] tracking-[-0.01em]">
                WHAT OTTAWA DRIVERS SAY
              </h2>
            </div>
            <a
              href={google.maps_url || 'https://www.google.com/search?q=A%2B+Auto+Glass+Ottawa'}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="reviews-google-badge"
              className="inline-flex items-center gap-4 rounded-2xl bg-white border border-slate-100 px-6 py-4 shadow-[0_6px_24px_rgba(15,23,42,0.07)] hover:shadow-[0_12px_32px_rgba(15,23,42,0.12)] transition-shadow"
            >
              <span className="text-[34px] font-black leading-none text-[#111827]">{Number(google.rating).toFixed(1)}</span>
              <span>
                <Stars size="w-[18px] h-[18px]" />
                <span className="block mt-1 text-[13px] text-[#5F6773] font-medium">
                  on Google{google.total_reviews ? ` · ${google.total_reviews} reviews` : ''}
                </span>
              </span>
            </a>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-7">
            {google.reviews.slice(0, 3).map((r, i) => (
              <article
                key={i}
                data-testid={`desktop-review-card-${i}`}
                className="bg-white rounded-2xl border border-slate-100 p-8 shadow-[0_6px_24px_rgba(15,23,42,0.06)] flex flex-col"
              >
                <Quote className="w-7 h-7 text-[#0D5BD7]/30 fill-[#0D5BD7]/20" />
                <p className="mt-4 text-[15px] text-[#111827] leading-[1.7] flex-1">{r.text}</p>
                <div className="mt-6 flex items-center gap-3">
                  {r.author_photo ? (
                    <img src={r.author_photo} alt={r.author} referrerPolicy="no-referrer" className="w-11 h-11 rounded-full object-cover" />
                  ) : (
                    <div className="w-11 h-11 rounded-full bg-[#0D5BD7] text-white font-bold flex items-center justify-center">{r.author?.[0]}</div>
                  )}
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-[14px] font-bold text-[#111827] truncate">{r.author}</span>
                      <Stars n={r.rating || 5} size="w-3.5 h-3.5" />
                    </div>
                    <div className="text-[12px] text-[#5F6773]">{r.location || r.time_ago || 'Google review'}</div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <Footer />

      <QuoteRequestModal open={quoteOpen} onClose={() => setQuoteOpen(false)} />
    </div>
  );
}

function AudiencePanel({ image, eyebrow, title, desc, ctaLabel, href, testId }) {
  return (
    <a
      href={href}
      data-testid={testId}
      className="group relative block h-[440px] xl:h-[520px] overflow-hidden"
    >
      <img src={image} alt={eyebrow} className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-[700ms]" />
      <div
        className="absolute inset-0"
        style={{ backgroundImage: 'linear-gradient(to right, rgba(10,18,32,0.95) 0%, rgba(10,18,32,0.82) 40%, rgba(10,18,32,0.25) 72%, rgba(10,18,32,0) 100%)' }}
      />
      <div className="relative h-full p-10 lg:p-14 flex flex-col justify-between max-w-[560px]">
        <div>
          <div className="text-[12px] font-bold tracking-[0.24em] text-[#7fb0ff]">{eyebrow}</div>
          <h3 className="mt-4 text-[32px] lg:text-[36px] font-black text-white leading-[1.1] tracking-[-0.01em]">{title}</h3>
          <p className="mt-5 text-[16px] text-white/85 leading-[1.6] max-w-[380px]">{desc}</p>
        </div>
        <span className="inline-flex items-center gap-3 h-[52px] w-fit px-7 rounded-full bg-white text-[#111827] text-[12px] font-bold tracking-[0.2em] shadow-md group-hover:bg-[#0D5BD7] group-hover:text-white transition-colors">
          {ctaLabel}
          <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
        </span>
      </div>
    </a>
  );
}
